import React, { createContext, useContext, useReducer, useCallback } from 'react';
import { toToastSafeMessage } from '../utils/serviceError';

/**
 * FeedbackContext.js
 *
 * This module provides a global feedback system for displaying toast notifications and
 * a shared feedback modal throughout the application. It uses React Context + useReducer
 * to maintain a centralized state for:
 *   - `toasts`: An array of toast objects (each with an ID, variant, title, copy, etc.).
 *   - `modal`: A single modal configuration (visibility, variant, title, copy, button label/callback).
 *
 * Exports:
 *  - `FeedbackProvider`: A context provider that wraps the application and exposes:
 *       - `showToast({ variant, title, copy, duration })`: Adds a new toast to the queue.
 *         The toast auto-dismisses after `duration`.
 *       - `hideToast(id)`: Closes a specific toast before its auto-dismiss time.
 *       - `showModal({ variant, title, copy, buttonMessage, onClickButton })`: Displays a feedback modal.
 *       - `hideModal()`: Closes the currently active modal.
 *  - `useFeedbackContext()`: A custom hook to access these functions and the current state.
 *
 * Usage:
 *  1) Wrap your app in <FeedbackProvider> to enable global access.
 *  2) Inside your components, call `showToast()` or `showModal()` from `useFeedbackContext()`
 *     to display feedback messages. The actual UI (ToastFeedbackNotification, FeedbackModal)
 *     will read the state from this context.
 */
const SHOW_TOAST = 'SHOW_TOAST';
const START_CLOSE_TOAST = 'START_CLOSE_TOAST';
const HIDE_TOAST = 'HIDE_TOAST';
const SHOW_MODAL = 'SHOW_MODAL';
const HIDE_MODAL = 'HIDE_MODAL';

const initialFeedbackState = {
  toasts: [],
  modal: {
    isVisible: false,
    variant: '',
    title: '',
    copy: '',
    buttonMessage: '',
    onClickButton: null,
  },
};

function feedbackReducer(state, action) {
  switch (action.type) {
    case SHOW_TOAST:
      return {
        ...state,
        toasts: [...state.toasts, action.payload],
      };

    case START_CLOSE_TOAST: {
      const toastIdToClose = action.payload;
      const updatedToasts = state.toasts.map((toast) =>
        toast.id === toastIdToClose ? { ...toast, isClosing: true } : toast,
      );
      return { ...state, toasts: updatedToasts };
    }

    case HIDE_TOAST: {
      const toastIdToHide = action.payload;
      return {
        ...state,
        toasts: state.toasts.filter((toast) => toast.id !== toastIdToHide),
      };
    }

    case SHOW_MODAL:
      return {
        ...state,
        modal: {
          isVisible: true,
          variant: action.payload.variant,
          title: action.payload.title,
          copy: action.payload.copy,
          buttonMessage: action.payload.buttonMessage,
          onClickButton: action.payload.onClickButton,
        },
      };

    case HIDE_MODAL:
      return {
        ...state,
        modal: {
          ...state.modal,
          isVisible: false,
        },
      };

    default:
      return state;
  }
}

const FeedbackContext = createContext(null);

export function useFeedbackContext() {
  return useContext(FeedbackContext);
}

export function FeedbackProvider({ children }) {
  const [state, dispatch] = useReducer(feedbackReducer, initialFeedbackState);

  const showToast = useCallback(
    ({ variant = 'success', title = '', copy = '', duration = 3000 }) => {
      const rawCopy = copy != null && typeof copy !== 'string' ? String(copy) : copy;
      const safeCopy = variant === 'error' ? toToastSafeMessage(rawCopy) : rawCopy;
      const id = Date.now();
      dispatch({
        type: SHOW_TOAST,
        payload: { id, variant, title, copy: safeCopy, duration, isClosing: false },
      });

      setTimeout(() => {
        dispatch({ type: START_CLOSE_TOAST, payload: id });
        setTimeout(() => {
          dispatch({ type: HIDE_TOAST, payload: id });
        }, 300);
      }, duration);
    },
    [dispatch],
  );

  const hideToast = useCallback(
    (id) => {
      dispatch({ type: START_CLOSE_TOAST, payload: id });
      setTimeout(() => {
        dispatch({ type: HIDE_TOAST, payload: id });
      }, 300);
    },
    [dispatch],
  );

  const showModal = useCallback(
    ({ variant = 'success', title = '', copy = '', buttonMessage = '', onClickButton = null }) => {
      dispatch({
        type: SHOW_MODAL,
        payload: { variant, title, copy, buttonMessage, onClickButton },
      });
    },
    [dispatch],
  );

  const hideModal = useCallback(() => {
    dispatch({ type: HIDE_MODAL });
  }, [dispatch]);

  const value = React.useMemo(
    () => ({
      state,
      showToast,
      hideToast,
      showModal,
      hideModal,
    }),
    [state, showToast, hideToast, showModal, hideModal],
  );

  return <FeedbackContext.Provider value={value}>{children}</FeedbackContext.Provider>;
}
